import { useEffect, type RefObject } from 'react';

/**
 * Closes an overlay when the page behind it scrolls (orbit-ui#179).
 *
 * **Why it does not close itself.** A dropdown's panel is rendered into
 * `document.body` and positioned once, against where its button was. The
 * content scrolls in its own container, so the button moves and the panel
 * does not — leaving a menu floating over the middle of the page, pointing
 * at nothing. Reported on the account menu; the notification panel is the
 * same component with the same cause.
 *
 * **Capture, because the scroll is not on `window`.** The app's content
 * scrolls in a div, and a scroll event does not bubble — so a listener on
 * `window` never hears it. Captured at the document it hears every scroll
 * container, which is exactly the set of things that can move the button.
 *
 * **Passive**, so it never delays a scroll it does not cancel.
 *
 * ## `panel` — and why it is not optional in practice
 *
 * The first version listened to every scroll anywhere, which closed the
 * notification panel the moment somebody scrolled **inside it** to read
 * their older notifications (orbit-ui#180). That is the one scroll that must
 * not close it: the panel has not moved relative to its button, so there is
 * nothing to correct.
 *
 * Pass a ref to the panel's own root whenever the overlay scrolls internally.
 * A short menu that cannot scroll may leave it out.
 */
export function useCloseOnScroll(
  open: boolean,
  close: () => void,
  panel?: RefObject<HTMLElement | null>,
): void {
  useEffect(() => {
    if (!open) return;

    const onScroll = (event: Event) => {
      /*
        A scroll inside the overlay is the user reading it, not the page
        moving under it. `contains` rather than an identity check because the
        scrolling element is usually a container several levels down from the
        panel root.
      */
      const root = panel?.current;
      if (root && event.target instanceof Node && root.contains(event.target)) return;

      close();
    };

    document.addEventListener('scroll', onScroll, { capture: true, passive: true });

    return () => document.removeEventListener('scroll', onScroll, { capture: true });
  }, [open, close, panel]);
}
